import { OfferData } from '../../constants/apiResponses.types';
import { getCurrentSubRollingOffer } from '../../utils';

const BUY_ONE_GET_MANY_EVENT_TYPE = 'one_plus_many';

const BLOCK_TO_OFFERS_KEY: Record<string, keyof IBuyOneGetManyOffersByBlock> = {
  onePlusMany: 'onePlusMany',
  progressBars: 'progressBars',
  specialOffers: 'promotions',
  rollingOffers: 'rolling',
  bundles: 'bundles',
};

export interface IBuyOneGetManyOffersByBlock {
  onePlusMany?: OfferData[];
  progressBars?: OfferData[];
  promotions?: OfferData[];
  rolling?: OfferData[];
  bundles?: OfferData[];
}

export interface IBuyOneGetManyEventSharedContext {
  os?: string;
  browser?: string;
  countryCode?: string;
}

const countPrecedingOffers = (
  order: string[],
  onePlusManyBlockPosition: number,
  offers: IBuyOneGetManyOffersByBlock,
): number => {
  let precedingOffersCount = 0;
  for (let blockIndex = 0; blockIndex < onePlusManyBlockPosition; blockIndex++) {
    const offersKey = BLOCK_TO_OFFERS_KEY[order[blockIndex]];
    if (offersKey === undefined || offersKey === 'onePlusMany') continue;
    precedingOffersCount += offers[offersKey]?.length ?? 0;
  }
  return precedingOffersCount;
};

export const computeBuyOneGetManyOfferPosition = (
  offerId: string,
  bodyBlocksOrder: string[] | undefined,
  offers: IBuyOneGetManyOffersByBlock,
): number | undefined => {
  const onePlusManyList = offers.onePlusMany ?? [];
  const withinBlockIndex = onePlusManyList.findIndex(
    (offer) => offer.offerId === offerId,
  );
  if (withinBlockIndex === -1) return undefined;

  const order = bodyBlocksOrder ?? [];
  const onePlusManyBlockPosition = order.indexOf('onePlusMany');
  if (onePlusManyBlockPosition === -1) return withinBlockIndex + 1;

  const precedingOffersCount = countPrecedingOffers(order, onePlusManyBlockPosition, offers);
  return precedingOffersCount + withinBlockIndex + 1;
};

interface IBuildBuyOneGetManyEventPropsArgs {
  offer: OfferData;
  offers: IBuyOneGetManyOffersByBlock;
  bodyBlocksOrder: string[] | undefined;
  sharedContext: IBuyOneGetManyEventSharedContext;
}

export const buildBuyOneGetManyEventProps = ({
  offer,
  offers,
  bodyBlocksOrder,
  sharedContext,
}: IBuildBuyOneGetManyEventPropsArgs): Record<string, unknown> => {
  const tile = getCurrentSubRollingOffer(offer);
  const prices = tile?.prices;
  const priceInUsdCents = prices?.priceInUsdCents ?? 0;
  const localPriceAmount = prices?.price ?? 0;

  const tileProducts = tile?.products ?? [];
  const products = tileProducts.map((product) => ({
    product_id: product.publisherProductId,
    product_name: product.name,
    product_quantity: product.quantity,
  }));

  const salePercentage = offer.productSale?.sale ?? offer.dynamicOfferUi?.salePercentage;

  return {
    offer_id: offer.offerId,
    offer_name: offer.offerName,
    offer_external_id: offer.publisherOfferId,
    currency: prices?.currencyCode,
    products,
    sale_percentage: salePercentage,
    type: BUY_ONE_GET_MANY_EVENT_TYPE,
    sub_type: offer.subType === undefined ? null : offer.subType,
    amount_gross_usd: priceInUsdCents / 100,
    amount_gross_local: localPriceAmount,
    total_tiles: offer.productsSequence.length,
    index: offer.indexToCollect,
    is_free: priceInUsdCents === 0,
    offer_position: computeBuyOneGetManyOfferPosition(
      offer.offerId,
      bodyBlocksOrder,
      offers,
    ),
    os: sharedContext.os,
    browser: sharedContext.browser,
    country_code: sharedContext.countryCode,
    url: window.location.origin,
  };
};
